import { MantineColor } from '@mantine/core';
import React, { useMemo } from 'react';
import { Row } from 'react-table';

import {
  CustomerWithControlledType,
  useIsChannelPartner,
} from '@portals/api/partners';
import { usePortalProtocolAndDomain } from '@portals/framework';
import {
  RouteModalLink,
  useOpenRouteModal,
} from '@portals/framework/route-modals';
import { ReactComponent as Danger } from '@portals/icons/linear/danger.svg';
import { ReactComponent as Export2 } from '@portals/icons/linear/export-2.svg';
import { ReactComponent as MonitorMobbile } from '@portals/icons/linear/monitor-mobbile.svg';
import { ReactComponent as Ticket } from '@portals/icons/linear/ticket.svg';
import { ReactComponent as UserSquare } from '@portals/icons/linear/user-square.svg';
import {
  AvatarCell,
  BadgeCell,
  RowMenuItem,
  RowMenuItems,
  SmartTable,
  TextWithIconCell,
} from '@portals/table';
import {
  PaginatedFilterTypeEnum,
  RowMenuRefType,
  TableColumn,
  TableColumnSortValue,
} from '@portals/types';

const CUSTOMER_SEVERITY_TYPE: Record<
  CustomerWithControlledType['severity_status'],
  { id: number; title: string; color: MantineColor }
> = {
  high: { id: 3, title: 'High', color: 'red' },
  medium: { id: 2, title: 'Medium', color: 'orange' },
  low: { id: 1, title: 'Low', color: 'teal' },
};

const sortBySeverity: TableColumnSortValue<CustomerWithControlledType> = (
  rowA,
  rowB
) => {
  const rowAValues = rowA.original;
  const rowBValues = rowB.original;

  const { id: rowASeverity } =
    CUSTOMER_SEVERITY_TYPE[rowAValues.severity_status];
  const { id: rowBSeverity } =
    CUSTOMER_SEVERITY_TYPE[rowBValues.severity_status];

  const rowAAdjustedValue = rowASeverity > rowBSeverity ? 1 : 0;
  const rowBAdjustedValue = rowBSeverity > rowASeverity ? 1 : 0;

  return rowAAdjustedValue - rowBAdjustedValue;
};

interface CustomersListViewProps {
  customers: Array<CustomerWithControlledType>;
}

export function CustomersListView({ customers }: CustomersListViewProps) {
  const { protocolAndDomain } = usePortalProtocolAndDomain();
  const openRouteModal = useOpenRouteModal();

  const isChannelPartner = useIsChannelPartner();

  const columns = useMemo(() => {
    const tableColumns: Array<TableColumn<CustomerWithControlledType>> = [
      {
        dataField: 'name',
        text: 'Name',
        sort: true,
        formatter: (_, { name, id, logo_url }) => (
          <RouteModalLink modalId="customer" pathParams={[id]}>
            <AvatarCell src={logo_url} label={name} radius="md" />
          </RouteModalLink>
        ),
      },
      {
        dataField: 'devices_count',
        text: 'Devices',
        sort: true,
        formatter: (_, row) => (
          <TextWithIconCell
            Icon={MonitorMobbile}
            label={row.devices_count.toString()}
            spacing="xs"
            color="primary"
            textProps={{
              component: 'a',
              href: `${protocolAndDomain}/devices?org=${row.id}`,
              target: '_blank',
            }}
          />
        ),
      },
      {
        dataField: 'incidents_count',
        text: 'Incidents',
        minWidth: 200,
        sort: true,
        formatter: (_, row) => {
          return (
            <TextWithIconCell
              Icon={Danger}
              label={row.incidents_count.toString()}
              spacing="xs"
              color="primary"
              textProps={{
                component: 'a',
                href: `${protocolAndDomain}/incidents?org=${row.id}`,
                target: '_blank',
              }}
            />
          );
        },
      },
      {
        dataField: 'tickets_count',
        text: 'Tickets',
        sort: true,
        formatter: (_, row) => (
          <TextWithIconCell
            Icon={Ticket}
            label={row.tickets_count.toString()}
            spacing="xs"
            color="primary"
            textProps={{
              component: 'a',
              href: `${protocolAndDomain}/tickets?org=${row.id}`,
              target: '_blank',
            }}
          />
        ),
      },
      {
        dataField: 'severity_status',
        text: 'Status',
        sort: true,
        sortValue: (rowA, rowB) => sortBySeverity(rowA, rowB),
        formatter: (cell, row) => {
          const { color, title } = CUSTOMER_SEVERITY_TYPE[row.severity_status];

          return <BadgeCell color={color} label={`${title} Severity`} />;
        },
      },
    ];

    if (isChannelPartner) {
      tableColumns.push({
        dataField: 'manufacturer',
        text: 'Manufacturer',
        sort: true,
        filter: {
          type: PaginatedFilterTypeEnum.Text,
        },
        formatter: (_, row) => (
          <AvatarCell
            src={row.main_partner.logo_url}
            label={row.main_partner.display_name}
            radius="md"
            withAbbreviation={false}
          />
        ),
      });
    }

    return tableColumns;
  }, [isChannelPartner, protocolAndDomain]);

  const getMenuItems = (
    row: Row<CustomerWithControlledType>,
    menuRef: RowMenuRefType
  ) => {
    const items: Array<RowMenuItem> = [
      {
        id: 'view',
        onClick: () => {
          openRouteModal({
            modalId: 'customer',
            pathParams: [row.original.id],
          });
          menuRef.onClose();
        },
        label: 'View Customer',
        Icon: UserSquare,
      },
    ];

    if (row.original.controlled) {
      items.push({
        id: 'visit',
        onClick: () =>
          window.open(
            `${protocolAndDomain}?org=${row.original.id}`,
            `_blank`,
            'noopener noreferrer'
          ),
        label: 'Visit Portal',
        Icon: Export2,
      });
    }

    return items;
  };

  return (
    <SmartTable<CustomerWithControlledType>
      keyField="id"
      name="partners.customers"
      data={customers}
      columns={columns}
      isUrlSyncEnabled={false}
      noDataIndication={{ title: 'No customers' }}
      menuProps={{ position: 'left' }}
      rowMenu={({ row, wrapperProps }) => (
        <RowMenuItems
          wrapperProps={wrapperProps}
          labelSize="sm"
          items={getMenuItems(row, wrapperProps.menuRef)}
        />
      )}
    />
  );
}
